import { theme, Space, Tag, Tooltip, Typography, Row } from "antd";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { stripMarkdown } from "../cardUtils";
import { CustomTooltip } from "src/components/CustomTooltip";

export type StatusType = "improved" | "regressed" | "neutral";
export type TrendType = "up" | "down" | "neutral";

export interface ActivityItemProps {
  description: string;
  title?: string;
  evidence?: Array<any>;
  tags?: string[];
  cta?: string;
  ctaTooltip?: string;
  className?: string;
  /**
   * Layout variant for the item: 'default' (full), 'compact', 'minimal'
   */
  variant?: "default" | "compact" | "minimal" | "list";
}

export const ActivityItem: React.FC<ActivityItemProps> = ({
  description,
  title,
  evidence,
  tags,
  cta,
  ctaTooltip,
  className,
  variant = "default",
}) => {
  const { token } = theme.useToken();

  if (variant === "compact") {
    return (
      <div
        className={[className, "vzbl-activity-compact"]
          .filter(Boolean)
          .join(" ")}
        style={{ display: "flex", alignItems: "center", columnGap: 8 }}
      >
        {cta &&
          (ctaTooltip ? (
            <Tooltip title={ctaTooltip}>
              <Tag className="vzbl-pill-tag">{cta}</Tag>
            </Tooltip>
          ) : (
            <Tag className="vzbl-pill-tag">{cta}</Tag>
          ))}
      </div>
    );
  }
  if (variant === "minimal") {
    return (
      <div
        className={[className, "vzbl-activity-minimal", "vzbl-text-3"]
          .filter(Boolean)
          .join(" ")}
      >
        <Markdown
          remarkPlugins={[remarkGfm]}
          skipHtml={true}
          className="vzbl-text-3"
        >
          {description}
        </Markdown>
      </div>
    );
  }
  if (variant === "list") {
    return (
      <div
        className={[className, "vzbl-activity-list", "vzbl-text-3"]
          .filter(Boolean)
          .join(" ")}
      >
        <Markdown
          remarkPlugins={[remarkGfm]}
          skipHtml={true}
          className="vzbl-text-3"
        >
          {description}
        </Markdown>
      </div>
    );
  }
  // Default full item
  return (
    <div
      className={className}
      style={{
        width: "100%",
      }}
    >
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Space
          direction="vertical"
          size="small"
          style={{
            width: "100%",
            alignItems: "top",
          }}
        >
          {title && (
            <div
              style={{
                display: "flex",
                alignItems: "top",
                columnGap: 8,
                justifyContent: "space-between",
              }}
            >
              <Typography.Text className="vzbl-header-5 single-line">
                <CustomTooltip content={stripMarkdown(title)}>
                  {stripMarkdown(title)}
                </CustomTooltip>
              </Typography.Text>
              {cta &&
                (ctaTooltip ? (
                  <Tooltip title={ctaTooltip}>
                    <Tag className="vzbl-pill-tag">{cta}</Tag>
                  </Tooltip>
                ) : (
                  <Tag className="vzbl-pill-tag">{cta}</Tag>
                ))}
            </div>
          )}
          <Markdown
            remarkPlugins={[remarkGfm]}
            skipHtml={true}
            className="vzbl-text-3"
          >
            {description}
          </Markdown>
          {evidence && evidence.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {evidence.map((item, idx) => (
                <li key={idx}>
                  <Markdown
                    remarkPlugins={[remarkGfm]}
                    skipHtml={true}
                    className="vzbl-text-3"
                  >
                    {item}
                  </Markdown>
                </li>
              ))}
            </ul>
          )}
        </Space>
        <Space align="start" style={{ width: "100%" }}>
          {tags &&
            tags.map((tag, index) => (
              <Tag className="vzbl-pill-tag" key={index}>
                {stripMarkdown(tag)}
              </Tag>
            ))}
        </Space>
      </Space>
    </div>
  );
};
